import { Component } from '@theme/component';

/**
 * <bundle-add-to-cart>
 *
 * Independent submit button. Communicates with <bundle-pack> and
 * <bundle-subscribe> via three custom events on `document`:
 *   - bundlePack:totalChanged   (in)  — refresh price + qty + validity
 *   - bundleSubscribe:change    (in)  — store mode/frequency/discount for the price label
 *   - bundlePack:requestSubmit  (out) — bundle-pack runs its POST
 *
 * No JS imports between blocks. Each block is self-contained and the only
 * coupling is the event names + payload shape, both documented here.
 *
 * @typedef {object} BundleAddToCartRefs
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} submitLabel
 * @property {HTMLElement} submitPrice
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} liveRegion
 *
 * @extends Component<BundleAddToCartRefs>
 */
class BundleAddToCart extends Component {
  requiredRefs = ['submitButton', 'submitLabel', 'submitPrice', 'errorMessage', 'liveRegion'];

  #abortController = new AbortController();
  #moneyFormat = '{{amount}} €';
  #lockedTextTemplate = '';
  #offTierTextTemplate = '';
  #emptyText = '';
  #addText = '';

  /** Latest known totals from <bundle-pack>. */
  #subtotal = 0;
  #tierTotal = 0;
  #quantity = 0;
  #minQty = 0;
  /** @type {number[]} Quantities that mark a valid tier (e.g. [7, 10, 15, 20]). */
  #tierQtys = [];

  /** Latest known mode from <bundle-subscribe>. */
  #subscribeMode = 'one-time';
  #subscribeDiscountPct = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}} €';
    this.#lockedTextTemplate = this.dataset.lockedText || 'Choose {remaining} more';
    this.#offTierTextTemplate = this.dataset.offTierText || '';
    this.#emptyText = this.dataset.emptyText || 'Select a product';
    this.#addText = this.dataset.addText || 'Add to cart';
    this.#minQty = Number(this.dataset.minTotalQuantity || 0);

    const { signal } = this.#abortController;

    document.addEventListener(
      'bundlePack:totalChanged',
      /** @param {Event} event */ (event) => this.#onBundleTotal(event),
      { signal }
    );
    document.addEventListener(
      'bundleSubscribe:change',
      /** @param {Event} event */ (event) => this.#onSubscribeChange(event),
      { signal }
    );
    document.addEventListener(
      'bundlePack:submitDone',
      /** @param {Event} event */ (event) => this.#onSubmitDone(event),
      { signal }
    );
    document.addEventListener(
      'bundlePack:submitError',
      /** @param {Event} event */ (event) => this.#onSubmitError(event),
      { signal }
    );

    this.addEventListener('click', this.#onClick, { signal });

    // Initial render — button shows the "empty" state until the bundle reports
    // some quantity.
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {Event} event */
  #onBundleTotal(event) {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    this.#subtotal = Number(detail.subtotal) || 0;
    this.#tierTotal = Number(detail.discountedTotal) || 0;
    this.#quantity = Number(detail.quantity) || 0;
    if (Array.isArray(detail.tierQtys)) {
      this.#tierQtys = detail.tierQtys.map(Number).filter((n) => n > 0);
    }
    this.#render();
  }

  /** @param {Event} event */
  #onSubscribeChange(event) {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    this.#subscribeMode = detail.mode === 'subscribe' ? 'subscribe' : 'one-time';
    this.#subscribeDiscountPct = Number(detail.discountPercentage) || 0;
    this.#render();
  }

  /** @param {Event} event */
  #onSubmitDone(_event) {
    // Reset state — the bundle-pack handles its own UI reset, we just clear errors.
    this.refs.errorMessage.textContent = '';
    this.refs.submitButton.disabled = false;
  }

  /** @param {Event} event */
  #onSubmitError(event) {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    this.refs.errorMessage.textContent =
      detail.message || this.dataset.genericErrorText || '';
    this.refs.liveRegion.textContent = this.refs.errorMessage.textContent;
    this.refs.submitButton.disabled = false;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('[data-bundle-add-to-cart-submit]')) return;
    event.preventDefault();

    if (this.refs.submitButton.disabled) return;

    // Disable while the request is in flight, until bundle-pack tells us
    // it's done (success or error).
    this.refs.submitButton.disabled = true;
    this.refs.errorMessage.textContent = '';

    document.dispatchEvent(new CustomEvent('bundlePack:requestSubmit'));
  };

  #render() {
    const remaining = Math.max(0, this.#minQty - this.#quantity);
    const meetsMin = remaining === 0 && this.#quantity > 0;

    if (!meetsMin) {
      this.refs.submitButton.disabled = true;
      this.refs.submitLabel.textContent =
        this.#quantity === 0
          ? this.#emptyText
          : this.#lockedTextTemplate.replace('{remaining}', String(remaining));
      this.refs.submitPrice.textContent = '';
      return;
    }

    // Min is met — but if tiers are fixed (e.g. 7/10/15/20), the total must
    // either match one of them exactly OR be above the last one (no upper cap).
    // Only quantities strictly between two tiers are rejected.
    const maxTier = this.#tierQtys.length > 0 ? Math.max(...this.#tierQtys) : 0;
    const onTier =
      this.#tierQtys.length === 0 ||
      this.#tierQtys.includes(this.#quantity) ||
      this.#quantity > maxTier;
    if (!onTier && this.#offTierTextTemplate) {
      this.refs.submitButton.disabled = true;
      this.refs.submitLabel.textContent = this.#offTierTextTemplate.replace(
        '{tiers}',
        this.#formatTierList()
      );
      this.refs.submitPrice.textContent = '';
      return;
    }

    this.refs.submitButton.disabled = false;
    this.refs.submitLabel.textContent = this.#addText;

    // Cumulated price = tier-discounted total × (1 - subscribeDiscount%) when
    // subscribe mode is active.
    const finalPrice =
      this.#subscribeMode === 'subscribe' && this.#subscribeDiscountPct > 0
        ? Math.round(this.#tierTotal * (1 - this.#subscribeDiscountPct / 100))
        : this.#tierTotal;

    // The strikethrough is the subtotal *before* tier discount when the bundle
    // grants one, OR the tier total when only the subscription discount applies.
    let originalPrice = 0;
    if (this.#subtotal > finalPrice) {
      // Choose the largest "before discount" so customer always sees the savings.
      originalPrice = Math.max(this.#subtotal, this.#tierTotal);
    }

    if (originalPrice > finalPrice) {
      // Final price first, struck-through "before" price to its right.
      this.refs.submitPrice.innerHTML = `
        <span>${this.#formatMoney(finalPrice)}</span>
        <span class="bundle-add-to-cart__price-original">${this.#formatMoney(originalPrice)}</span>
      `;
    } else {
      this.refs.submitPrice.textContent = this.#formatMoney(finalPrice);
    }
  }

  /** Builds a human-readable list like "7, 10, 15 ou 20" from the tier qtys. */
  #formatTierList() {
    const qtys = [...this.#tierQtys];
    if (qtys.length === 0) return '';
    if (qtys.length === 1) return String(qtys[0]);
    const last = qtys.pop();
    return qtys.join(', ') + ' ou ' + last;
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const value = cents / 100;
    /** @type {Record<string, () => string>} */
    const formatters = {
      amount: () => value.toFixed(2),
      amount_no_decimals: () => Math.round(value).toString(),
      amount_with_comma_separator: () => value.toFixed(2).replace('.', ','),
      amount_no_decimals_with_comma_separator: () =>
        Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      amount_with_space_separator: () =>
        value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
      amount_no_decimals_with_space_separator: () =>
        Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
      amount_with_apostrophe_separator: () =>
        value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'"),
    };
    return this.#moneyFormat.replace(/{{\s*(\w+)\s*}}/g, (_, key) =>
      formatters[key] ? formatters[key]() : value.toFixed(2).replace('.', ',')
    );
  }
}

if (!customElements.get('bundle-add-to-cart')) {
  customElements.define('bundle-add-to-cart', BundleAddToCart);
}
